import {
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  ChangeDetectionStrategy,
} from "@angular/core";
import {withUtm} from '../../shared/utils/with-utm';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { CompetitionDto } from "../../shared/services/CompetitionDto";
import { CompetitionPodiumDto } from "../../shared/services/CompetitionPodiumDto";
import { CompetitionService } from "../../shared/services/competition.service";
import { AuthenticationService } from "../../shared/services/authentication.service";
import { HttpResponse } from "@angular/common/http";
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  startWith,
  Subscription,
  switchMap,
  timeout,
} from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { ErrorDialogComponent } from "../../shared/components/error-dialog.component";
import { LocalStorageService } from "../../shared/services/local-storage.service";
import { AnalyticsService } from "../../shared/services/analytics.service";
import {
  AsyncPipe,
  CommonModule,
  DatePipe,
  isPlatformServer,
} from "@angular/common";
import { SharedMaterialModule } from "../../shared/shared-material.module";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatNativeDateModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { CompetitionStatus } from "../../shared/services/CompetitionStatus";
import { IntroSectionComponent } from "../../shared/layout/intro/intro.component";
import { PrivacyWarningDialogComponent } from "../../shared/components/privacy-warning-dialog.component";
import { ConnectNotionCtaComponent } from "../../shared/components/connect-notion-cta/connect-notion-cta.component";
import { ShareService } from "../../shared/services/share.service";
import { RouterLink } from "@angular/router";
import { isNumberedSeasonName, isSeasonOneName } from "../../shared/utils/season";

export type CompetitionConnectionState =
  | "checking"
  | "connected"
  | "disconnected";

/** Per-competition invite-email UI state (backlog #234 (2)): under cap-raise
 *  rule (b) a user can hold up to two competition cards at once, so the
 *  invite-email form control and its send state can no longer be shared
 *  fields on the component — each card needs its own. */
export interface InviteEmailState {
  control: FormControl<string | null>;
  sending: boolean;
  sentTo: string | null;
  error: string | null;
}

export interface PodiumRow {
  place: 1 | 2 | 3;
  label: string;
  name: string;
  value: string;
  isYou: boolean;
}

export interface PodiumCategory {
  key: "totalPoints" | "completionRate" | "perfectDays";
  label: string;
  rows: PodiumRow[];
}

const PLACE_LABELS: Record<number, string> = {
  1: "🏆 1st Place",
  2: "2nd Place",
  3: "3rd Place",
};

function perfectDaysText(n: number): string {
  return n === 1 ? "1 Perfect Day" : `${n} Perfect Days`;
}

function buildRows(
  names: string[] | null | undefined,
  values: number[] | null | undefined,
  format: (n: number) => string,
  userName: string
): PodiumRow[] {
  const rows: PodiumRow[] = [];
  (names ?? []).slice(0, 3).forEach((name, i) => {
    const v = values?.[i];
    if (v === undefined || v === null) return;
    rows.push({
      place: (i + 1) as 1 | 2 | 3,
      label: PLACE_LABELS[i + 1],
      name,
      value: format(v),
      isYou: !!userName && name === userName,
    });
  });
  return rows;
}

/** Pure: the three podium categories of a podium, empty when there is nothing to show. */
export function buildPodiumCategories(
  podium: CompetitionPodiumDto | null
): PodiumCategory[] {
  const t = podium?.topThreeWinnersStats;
  if (!podium || !t) return [];
  const u = podium.userName;
  return [
    {
      key: "totalPoints" as const,
      label: "Total Points",
      rows: buildRows(t.topTotalPointsWinners, t.topTotalPoints, (n) => `${n} points`, u),
    },
    {
      key: "completionRate" as const,
      label: "Completion Rate",
      rows: buildRows(t.topAveragePercentageWinners, t.topAveragePercentages, (n) => `${n.toFixed(2)}%`, u),
    },
    {
      key: "perfectDays" as const,
      label: "Perfect Days",
      rows: buildRows(t.topPerfectDaysWinners, t.topPerfectDays, perfectDaysText, u),
    },
  ].filter((c) => c.rows.length > 0);
}

/** Pure: the caller's own standing line, or null when they have no rank. */
export function buildPodiumOwnLine(
  podium: CompetitionPodiumDto | null
): string | null {
  if (!podium || !podium.userTotalPointsRank) return null;
  return (
    `You: #${podium.userTotalPointsRank} by points (${podium.userTotalPoints} points) · ` +
    `#${podium.userCompletionRateRank} by completion rate (${podium.userCompletionRate.toFixed(2)}%) · ` +
    `#${podium.userPerfectDaysRank} by perfect days (${podium.userPerfectDays})`
  );
}

@Component({
  selector: "app-competitions",
  templateUrl: "./competition.component.html",
  styleUrls: ["./competition.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ReactiveFormsModule,
    AsyncPipe,
    DatePipe,
    SharedMaterialModule,
    CommonModule,
    MatButtonToggleModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatInputModule,
    MatFormFieldModule,
    IntroSectionComponent,
    ConnectNotionCtaComponent,
    RouterLink,
  ],
})
export class CompetitionComponent implements OnInit, OnDestroy {
  competitionForm!: FormGroup;
  searchForm!: FormGroup;
  myCompetitions: BehaviorSubject<CompetitionDto[]> = new BehaviorSubject<
    CompetitionDto[]
  >([]);
  joinedIds: Set<number> = new Set<number>();
  featuredCompetition: CompetitionDto | null = null;
  podium: CompetitionPodiumDto | null = null;
  podiumCategoryRows: PodiumCategory[] = [];
  podiumOwnLine: string | null = null;
  podiumIsSeasonZero = false;

  connectionState$ = new BehaviorSubject<CompetitionConnectionState>(
    "checking"
  );
  showCompetitionForm = false;
  // Both toggles start collapsed. This was flipped to true in "show featured
  // comp on search" (70aaf412) so the featured card would be visible on load,
  // back when that card lived inside the search section; it has since moved to
  // its own row above the toggle pair, so the expanded default is just noise.
  showCompetitionSearch = false;
  searchedCompetitions: CompetitionDto[] = [];
  isSubmittingCompetition = false;
  isJoiningCompetition = false;
  searchControl = new FormControl();
  searchSubscription?: Subscription;

  /** Keyed by competition id — see `inviteState()`. */
  private inviteEmailStates = new Map<number, InviteEmailState>();

  /** Competition ids with a leave request in flight. Same reason as
   *  `inviteEmailStates`: with two cards visible at once, a shared boolean
   *  would grey out the Leave button on the other card too. */
  private leavingCompetitionIds = new Set<number>();

  /** Competition ids whose invite panel is expanded. The panel (share link +
   *  invite-by-email) is collapsed by default: with up to two cards on
   *  screen, always-expanded invite markup was the single biggest block of
   *  vertical noise on this page. */
  private openInvites = new Set<number>();

  constructor(
    private competitionService: CompetitionService,
    private authService: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private storageService: LocalStorageService,
    private shareService: ShareService,
    private analyticsService: AnalyticsService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit() {
    // During SSR/prerender there is no localStorage to read the user's state
    // from. Render the neutral shell and re-run in the browser.
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.searchSubscription = this.searchControl.valueChanges
      .pipe(
        debounceTime(500), // Adjust the debounce time as needed
        distinctUntilChanged(),
        switchMap((searchTerm) => {
          if (searchTerm) {
            return this.competitionService.searchCompetitions(searchTerm);
          } else {
            // Return an empty array or fetch all competitions
            return of([]);
            // return this.competitionService.getAllCompetitions();
          }
        })
      )
      .subscribe({
        next: (results) => {
          this.searchedCompetitions = results;
        },
        error: (error) => {
          console.error("Search error:", error);
          // Display an error message to the user
        },
      });

    const email = this.storageService.getItem("email");
    const notionUserId = this.storageService.getItem("notionUserId");

    this.competitionForm = new FormGroup(
      {
        name: new FormControl(null, [
          Validators.required,
          Validators.maxLength(50),
        ]),
        description: new FormControl(null, [
          Validators.required,
          Validators.maxLength(300),
        ]),
        startDate: new FormControl(null, [this.startDateNotBeforeToday()]),
        endDate: new FormControl(null, Validators.required),
        password: new FormControl(null, Validators.maxLength(50)),
      },
      { validators: this.dateLessThan("startDate", "endDate") }
    );

    this.searchForm = new FormGroup({
      search: new FormControl(null, Validators.required),
    });

    if (notionUserId === null) {
      // No Notion workspace connected yet: nothing to check or fetch. Show
      // the connect-Notion CTA instead of the alert()+redirect that used to
      // run here.
      this.connectionState$.next("disconnected");
      return;
    }

    this.authService
      .userHasAccessToken(email)
      .pipe(
        timeout(5000),
        catchError((error) => {
          console.error("Failed to check access token", error);
          return of(false); // Handle errors and continue
        })
      )
      .subscribe((hasAccessToken) => {
        if (hasAccessToken === true) {
          this.connectionState$.next("connected");
          this.loadCompetitions(notionUserId);
        } else {
          this.connectionState$.next("disconnected");
        }
      });
  }

  private setMyCompetitions(list: CompetitionDto[]): void {
    this.myCompetitions.next(list);
    this.joinedIds = new Set(list.map((c) => c.id));
  }

  /** Cap-raise rule (b): the Create/Join gates open once the user has no
   *  REGULAR (non-featured) competition, regardless of whether they are in
   *  the featured one. */
  get hasRegularCompetition(): boolean {
    return this.myCompetitions.getValue().some((c) => !c.featured);
  }

  /** Lazily creates and returns the per-competition invite-email state.
   *  Backlog #234 (2): with up to two cards visible at once (one regular +
   *  the featured), this state can no longer live in shared component
   *  fields — see `InviteEmailState`. */
  inviteState(competitionId: number): InviteEmailState {
    let state = this.inviteEmailStates.get(competitionId);
    if (!state) {
      state = {
        control: new FormControl("", [Validators.required, Validators.email]),
        sending: false,
        sentTo: null,
        error: null,
      };
      this.inviteEmailStates.set(competitionId, state);
    }
    return state;
  }

  private loadCompetitions(notionUserId: string) {
    this.competitionService.getMyCompetitions(notionUserId).subscribe({
      next: (competitions) => this.setMyCompetitions(competitions),
      error: () => {
        // No active competitions is the normal case for most users — nothing to do.
        this.setMyCompetitions([]);
      },
    });

    this.competitionService
      .getFeaturedCompetition()
      .pipe(
        catchError((error) => {
          console.error("Failed to load featured competition:", error);
          return of(null);
        })
      )
      .subscribe((comp) => {
        this.featuredCompetition = comp;
      });

    this.competitionService
      .getCompetitionPodium(notionUserId)
      .pipe(
        catchError((error) => {
          console.error("Failed to load competition podium:", error);
          return of(null);
        })
      )
      .subscribe((podium) => {
        this.podium = podium;
        this.podiumCategoryRows = buildPodiumCategories(podium);
        this.podiumOwnLine = buildPodiumOwnLine(podium);
        this.podiumIsSeasonZero = /season 0/i.test(podium?.competitionName ?? "");
      });
  }

  onSubmit() {
    this.isSubmittingCompetition = true;
    const endDate = this.competitionForm.value.endDate;
    // Create a Date representing midnight UTC on the chosen end date.
    const fixedEndDate = new Date(
      Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
    );

    const startDate = this.competitionForm.value.startDate;
    let fixedStartDate = null;
    if (startDate) {
      fixedStartDate = new Date(
        Date.UTC(
          startDate.getFullYear(),
          startDate.getMonth(),
          startDate.getDate()
        )
      );
    }

    const password = this.competitionForm.value.password || null;
    const isPasswordProtected = !!password;

    const competition: CompetitionDto = new CompetitionDto(
      null!,
      this.competitionForm.value.name,
      this.competitionForm.value.description,
      fixedStartDate,
      fixedEndDate,
      this.storageService.getItem("notionUserId"),
      [],
      password,
      null!,
      null!,
      isPasswordProtected
    );
    this.competitionService.createCompetition(competition).subscribe({
      next: (value: HttpResponse<CompetitionDto>) => {
        this.setMyCompetitions([...this.myCompetitions.getValue(), value.body!]);
        this.competitionForm.reset();
        this.isSubmittingCompetition = false;
      },
      error: (error) => {
        this.isSubmittingCompetition = false;
        console.error("Failed to create competition.", error);
      },
    });
  }

  dateLessThan(startDateField: string, endDateField: string) {
    return (formGroup: AbstractControl): { [key: string]: any } | null => {
      const start = formGroup.get(startDateField)!;
      const end = formGroup.get(endDateField)!;

      if (
        start.value &&
        end.value &&
        new Date(end.value) < new Date(start.value)
      ) {
        end.setErrors({ dateLessThan: true });
        return { dateLessThan: true };
      }

      return null;
    };
  }

  startDateNotBeforeToday() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return null;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set the time to the start of the day
      const startDate = new Date(control.value);

      return startDate < today ? { startDateNotBeforeToday: true } : null;
    };
  }

  /** Expands / collapses one card's invite panel. */
  toggleInvite(competitionId: number): void {
    if (this.openInvites.has(competitionId)) {
      this.openInvites.delete(competitionId);
    } else {
      this.openInvites.add(competitionId);
    }
  }

  isInviteOpen(competitionId: number): boolean {
    return this.openInvites.has(competitionId);
  }

  /** True while this competition's leave request is in flight — per card,
   *  never shared. */
  isLeavingCompetition(competitionId: number): boolean {
    return this.leavingCompetitionIds.has(competitionId);
  }

  leaveCompetition(competitionId: number) {
    this.leavingCompetitionIds.add(competitionId);
    this.competitionService
      .leaveCompetition(
        this.storageService.getItem("notionUserId"),
        competitionId
      )
      .subscribe({
        next: () => {
          this.setMyCompetitions(
            this.myCompetitions.getValue().filter((c) => c.id !== competitionId)
          );
          this.leavingCompetitionIds.delete(competitionId);
          this.inviteEmailStates.delete(competitionId);
          this.openInvites.delete(competitionId);
        },
        error: (error) => {
          console.error("Failed to leave competition.", error);
          this.leavingCompetitionIds.delete(competitionId);
        },
      });
  }

  joinCompetition(competition: CompetitionDto) {
    // Show privacy warning dialog first
    const dialogRef = this.dialog.open(PrivacyWarningDialogComponent, {
      data: {
        competitionName: competition.name,
        isPublic: !competition.isPrivate,
      },
      disableClose: true,
      // Consent dialog: focus the container, not the confirm button, so a
      // reflexive Enter cannot join and expose habit names.
      autoFocus: "dialog",
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.proceedWithJoin(competition);
      }
    });
  }

  private proceedWithJoin(competition: CompetitionDto) {
    this.isJoiningCompetition = true;
    let password = null;
    if (competition.isPrivate) {
      password = prompt("Please enter the competition password:");
      // Check if the user clicked "Cancel" on the prompt
      if (password === null) {
        this.isJoiningCompetition = false;
        return;
      }
    }

    this.competitionService
      .joinCompetition(
        this.storageService.getItem("notionUserId"),
        competition.id,
        password
      )
      .subscribe({
        next: (value) => {
          this.setMyCompetitions([...this.myCompetitions.getValue(), value]);
          this.isJoiningCompetition = false;
          // Backlog #L023: same event as the /join page, attributed here.
          this.analyticsService.trackEvent("Competition Joined", {
            competitionId: Number(value?.id ?? competition.id),
            featured: value?.featured === true || competition.featured === true,
            source: "competition-page",
          });
        },
        error: (error) => {
          console.error("Failed to join competition.", error);
          this.isJoiningCompetition = false;
          if (error.status === 400) {
            this.dialog.open(ErrorDialogComponent, {
              data: { message: "Incorrect password. Please try again." },
            });
          } else if (error.status === 409) {
            this.dialog.open(ErrorDialogComponent, {
              data: {
                message:
                  "You are already in a competition. Leave it before joining another one.",
              },
            });
          } else {
            this.dialog.open(ErrorDialogComponent, {
              data: {
                message: "Something went wrong. Please try again later.",
              },
            });
          }
        },
      });
  }

  toggleCompetitionForm() {
    this.showCompetitionForm = !this.showCompetitionForm;
    this.cdr.detectChanges();
  }

  toggleCompetitionSearch() {
    this.showCompetitionSearch = !this.showCompetitionSearch;
    this.cdr.detectChanges();
  }

  copyInviteLink(inviteLink: string) {
    this.shareService.share(
      inviteLink,
      "Join my competition on Notion Habit Heroes",
      "Join my habit competition on Notion Habit Heroes!"
    );
  }

  /** Mirrors DuelComponent.sendInviteEmail. Per-competition state (see
   *  `inviteState()`) so sending on one card never touches another. */
  sendInviteEmail(competitionId: number | null): void {
    if (!competitionId) return;
    const state = this.inviteState(competitionId);
    const email = (state.control.value || "").trim();
    if (state.sending || state.control.invalid || !email) return;

    state.sending = true;
    state.error = null;
    const notionUserId = this.storageService.getItem("notionUserId");

    this.competitionService
      .sendCompetitionInviteEmail(notionUserId, competitionId, email)
      .subscribe({
        next: () => {
          state.sending = false;
          state.sentTo = email;
          state.error = null;
          state.control.reset("");
        },
        error: (error) => {
          state.sending = false;
          switch (error.status) {
            case 429:
              state.error =
                "You've used all 5 invite emails for this competition.";
              break;
            case 409:
              state.error = "This competition has already ended.";
              break;
            case 400:
              state.error =
                "Could not send the invite. Please check the email address and try again.";
              break;
            default:
              state.error = "Could not send the invite. Please try again later.";
          }
        },
      });
  }

  /**
   * Shares the featured competition, addressed by id rather than invite
   * token: GET /points/featuredcompetition always serializes `inviteToken`
   * as null for authenticated callers (backlog #207, password-bypass
   * protection), so there is no token available on this card and none
   * should be requested. Available to every viewer regardless of whether
   * they've joined — promoting the featured competition is the point.
   */
  shareFeaturedCompetition(): void {
    if (!this.featuredCompetition) {
      return;
    }
    this.shareService.share(
      withUtm(`https://notionhabitheroes.com/join?competitionId=${this.featuredCompetition.id}`, 'featured-competition', 'share'),
      "Join the featured competition on Notion Habit Heroes",
      `Join "${this.featuredCompetition.name}" on Notion Habit Heroes!`
    );
  }

  /** #200: every member's CompetitionDto carries the same inviteToken, so
   *  the invite link is buildable regardless of who created the competition. */
  buildInviteLink(inviteToken: string): string {
    return withUtm(`https://notionhabitheroes.com/join?inviteToken=${inviteToken}`, 'competition-invite', 'share');
  }

  ngOnDestroy() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  protected readonly CompetitionStatus = CompetitionStatus;

  protected isSeasonOne(name: string | null | undefined): boolean {
    return isSeasonOneName(name);
  }

  protected isNumberedSeason(name: string | null | undefined): boolean {
    return isNumberedSeasonName(name);
  }
}
